import is from '@sindresorhus/is';
import type { EventSourceClient } from 'eventsource-client';
import { createEventSource } from 'eventsource-client';
import Maybe from 'true-myth/maybe';

import type { AuthorizationTokenSource, Pathname, RequestHeaders } from '../http-fetcher/http-fetcher';
import { resolveAuthorizationHeaders } from '../http-fetcher/http-fetcher';
import type { HTTPMethod } from '../http-fetcher/http-method';

type Listener = (event: MessageEvent) => void;

export type RealtimeEventListener = {
    readonly addEventListener: (type: string, listener: Listener) => void;
    readonly start: () => void;
    readonly stop: () => void;
};

export type RealtimeEventListenerBuilder = {
    readonly build: ({
        autoReconnect,
        method,
        path,
        payload,
        requestHeaders,
    }: {
        readonly autoReconnect?: boolean;
        readonly method?: 'GET' | 'POST';
        readonly path: Pathname;
        readonly payload?: FormData | string;
        readonly requestHeaders?: RequestHeaders;
    }) => RealtimeEventListener;
    /** The default headers plus a freshly resolved Authorization header, for requests made outside a listener. */
    readonly resolveRequestHeaders: () => Promise<RequestHeaders>;
    set authorization(source: AuthorizationTokenSource);
    set requestHeaders(_requestHeaders: RequestHeaders);
};

const MAX_RETRIES = 10;

/**
 * Dispatched before `close` when a listener without auto-reconnect gets a non-2xx response. The
 * data is a JSON {@link RealtimeHttpError}, so the caller can tell a rejected request (busy,
 * forbidden, gone) from a dropped stream instead of retrying both the same way.
 */
export const REALTIME_HTTP_ERROR_EVENT = 'http-error';

export type RealtimeHttpError = {
    readonly retryAfterSeconds: number | null;
    readonly status: number;
};

const parseRetryAfterSeconds = (header: string | null): number | null => {
    if (is.null(header) || !/^\d+$/.test(header.trim())) return null;
    return Number(header.trim());
};

const buildListener: (
    baseUrl: URL,
    path: Pathname,
    requestHeaders: Maybe<RequestHeaders>,
    authorization: Maybe<AuthorizationTokenSource>,
    payload?: FormData | string,
    method?: HTTPMethod,
    autoReconnect?: boolean,
) => RealtimeEventListener = (
    baseUrl,
    path,
    requestHeaders,
    authorization,
    payload,
    method = 'POST',
    autoReconnect = true,
) => {
    const basePath = baseUrl.pathname.replace(/\/+$/, '');
    const extendedPath = path.replace(/^\/+/, '');
    const actualPath = new URL(`${basePath}/${extendedPath}`, baseUrl);

    const listeners = new Map<string, Set<Listener>>();
    let client: EventSourceClient | null = null;
    let retryCount = 0;

    const dispatch = (type: string, data: string): void => {
        const handlers = listeners.get(type);
        if (is.undefined(handlers)) {
            return;
        }
        const event = new MessageEvent(type, { data });
        for (const handler of handlers) {
            handler(event);
        }
    };

    const closeAndNotify = (): void => {
        client?.close();
        client = null;
        dispatch('close', '');
    };

    return {
        addEventListener: (type, listener) => {
            let set = listeners.get(type);
            if (is.undefined(set)) {
                set = new Set();
                listeners.set(type, set);
            }
            set.add(listener);
        },
        start: () => {
            if (!is.null(client)) {
                return;
            }
            retryCount = 0;
            client = createEventSource({
                body: payload,
                credentials: 'include',
                fetch: async (url, init) => {
                    const response = await globalThis.fetch(url, {
                        ...init,
                        headers: { ...(await resolveAuthorizationHeaders(authorization)), ...init?.headers },
                    });
                    if (response.ok || autoReconnect) return response;
                    const error: RealtimeHttpError = {
                        retryAfterSeconds: parseRetryAfterSeconds(response.headers.get('Retry-After')),
                        status: response.status,
                    };
                    dispatch(REALTIME_HTTP_ERROR_EVENT, JSON.stringify(error));
                    closeAndNotify();
                    // eventsource-client neither reconnects nor reports a disconnect after an abort,
                    // so the rejection is reported exactly once, above.
                    throw new DOMException('Realtime request rejected', 'AbortError');
                },
                headers: requestHeaders.unwrapOr({}),
                method,
                onConnect: () => {
                    retryCount = 0;
                    dispatch('open', '');
                },
                onDisconnect: () => {
                    if (!autoReconnect) {
                        closeAndNotify();
                    }
                },
                onMessage: ({ data, event }) => {
                    dispatch(event ?? 'message', data);
                },
                onScheduleReconnect: () => {
                    if (!autoReconnect) {
                        closeAndNotify();
                        return;
                    }
                    retryCount += 1;
                    if (retryCount > MAX_RETRIES) {
                        closeAndNotify();
                    }
                },
                url: actualPath.toString(),
            });
        },
        stop: () => {
            client?.close();
            client = null;
        },
    };
};

export const createRealtimeEventListenerBuilder: (baseUrl: URL) => RealtimeEventListenerBuilder = (baseUrl) => {
    let defaultRequestHeaders: Maybe<RequestHeaders> = Maybe.nothing();
    let authorization: Maybe<AuthorizationTokenSource> = Maybe.nothing();

    return {
        build: ({ autoReconnect = true, method = 'POST', path, payload, requestHeaders = {} }) =>
            buildListener(
                baseUrl,
                path,
                defaultRequestHeaders.map((defaultHeaders) => ({ ...defaultHeaders, ...requestHeaders })),
                authorization,
                payload,
                method,
                autoReconnect,
            ),
        resolveRequestHeaders: async () => ({
            ...defaultRequestHeaders.unwrapOr({}),
            ...(await resolveAuthorizationHeaders(authorization)),
        }),
        set authorization(source: AuthorizationTokenSource) {
            authorization = Maybe.just(source);
        },
        set requestHeaders(_requestHeaders: RequestHeaders) {
            defaultRequestHeaders = defaultRequestHeaders.mapOrElse(
                () => Maybe.of(_requestHeaders),
                (headers) =>
                    Maybe.of<RequestHeaders>({
                        ...headers,
                        ..._requestHeaders,
                    }),
            );
        },
    };
};
